// client/src/components/leagues/ScoreEntryCard.js
import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { getMatchStatusLabel } from '../../utils/matchStatus'
import { MATCH_FORMAT } from '../../constants/matchFormats'

const SCORE_PLACEHOLDER = '—' // em dash for unset scores

/**
 * ScoreEntryCard - Displays the score entry interface with score pills,
 * validation errors, and match summary
 */
export default function ScoreEntryCard({
  sets,
  playerNames,
  matchData,
  visibleSets,
  canEdit,
  validationErrors,
  prioritizedError,
  showValidationErrors,
  matchSummary,
  onSetClick,
  isFinalized,
  leagueId,
  setErrors,
  set3Ref,
  navigate,
  matchFormat,
}) {
  const hasAnyScores = sets.some((s) => s.left !== null || s.right !== null)
  const isProSet = matchFormat === MATCH_FORMAT.PRO_SET_8

  const formatSetsString = (ss) => {
    const trimmed = ss.filter((s) => s.left !== null || s.right !== null)
    if (trimmed.length === 0) return 'No scores yet'
    return trimmed.map((s) => `${s.left ?? 0}–${s.right ?? 0}`).join(' | ')
  }

  return (
    <div className="score-entry-card mb-4">
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">
            <span className="me-2">📊</span>
            {isFinalized ? 'Match Score' : 'Enter Score'}
          </h5>
        </div>
        <div className="card-body">
          {/* Finalized banner */}
          {isFinalized && (
            <div
              className="match-finalized-banner alert alert-success d-flex align-items-center justify-content-between mb-3"
              role="status"
            >
              <div>
                <strong className="me-2">
                  <span aria-hidden="true">🔒</span> Score finalized
                </strong>
                <span className="text-muted">
                  {getMatchStatusLabel(matchData?.status)}
                </span>
                {hasAnyScores && (
                  <span className="ms-2 fw-semibold">
                    • {formatSetsString(sets)}
                  </span>
                )}
                {matchData.submitted_by_first_name &&
                matchData.submitted_by_last_name ? (
                  <span className="d-block small text-muted mt-1">
                    Submitted by {matchData.submitted_by_first_name}{' '}
                    {matchData.submitted_by_last_name}
                  </span>
                ) : (
                  <span className="d-block small text-muted mt-1">
                    Submitted (user not recorded)
                  </span>
                )}
              </div>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => navigate(-1)}
              >
                Go back
              </button>
            </div>
          )}

          {/* Player Header Row */}
          <div className="score-pills-header">
            <div className="header-spacer"></div>
            <div className="player-header">
              <div className="badge bg-secondary mb-1">Away</div>
              <div className="player-name">
                {matchData?.away_player_id ? (
                  <Link
                    to={`/players/${matchData.away_player_id}/history`}
                    state={{
                      fromLeague: true,
                      leagueId: Number(leagueId),
                      divisionId: matchData?.division_id,
                    }}
                    className="text-decoration-none"
                    title="View away player's match history"
                  >
                    {playerNames.left}
                  </Link>
                ) : (
                  playerNames.left
                )}
              </div>
            </div>
            <div className="player-header">
              <div className="badge bg-primary mb-1">Home</div>
              <div className="player-name">
                {matchData?.home_player_id ? (
                  <Link
                    to={`/players/${matchData.home_player_id}/history`}
                    state={{
                      fromLeague: true,
                      leagueId: Number(leagueId),
                      divisionId: matchData?.division_id,
                    }}
                    className="text-decoration-none"
                    title="View home player's match history"
                  >
                    {playerNames.right}
                  </Link>
                ) : (
                  playerNames.right
                )}
              </div>
            </div>
          </div>

          {/* Score entry instructions */}
          {canEdit && (
            <div
              className="alert alert-info py-2 px-3 mt-3 mb-3"
              role="note"
              data-testid="score-entry-instructions"
            >
              <div className="fw-semibold mb-1">Scoring Tips</div>
              <div className="small mb-0">
                <div className="mb-1">
                  <strong>
                    {isProSet
                      ? 'Pro set format: one set to 8 games (win by 2).'
                      : 'We encourage playing all three sets!'}
                  </strong>
                  {!isProSet &&
                    ' It gives everyone more court time and better competition.'}
                </div>
                {isProSet ? (
                  <div>
                    <strong>Valid scores:</strong> <strong>8–0</strong> through{' '}
                    <strong>8–6</strong>, or <strong>9–8</strong>.
                  </div>
                ) : (
                  <div>
                    <strong>Tiebreakers:</strong> Whether it&apos;s a match
                    tiebreaker (instead of a third set) or a set tiebreaker (in
                    the third set), enter it as <strong>7–6</strong> or{' '}
                    <strong>6–7</strong>. Don&apos;t enter tiebreak points (like
                    7–4) in the game score fields.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Set Score Rows */}
          {visibleSets.map((setIndex) => {
            const s = sets[setIndex]
            const isValid = !showValidationErrors || !setErrors[setIndex]
            const hasLeftValue = s.left !== null
            const hasRightValue = s.right !== null
            return (
              <div
                key={setIndex}
                className="set-score-row"
                ref={setIndex === 2 ? set3Ref : null}
              >
                <div className="set-label">
                  {isProSet ? 'Pro Set' : `Set ${setIndex + 1}`}
                </div>
                <button
                  className={`score-pill ${!isValid ? 'invalid' : ''} ${
                    !canEdit ? 'read-only' : ''
                  } ${hasLeftValue ? 'has-value' : ''}`}
                  onClick={() => canEdit && onSetClick(setIndex)}
                  disabled={!canEdit}
                  aria-label={`Enter score for set ${setIndex + 1}, ${playerNames.left}${!canEdit ? ' (match finalized)' : ''}`}
                  aria-describedby={
                    !canEdit ? 'match-finalized-notice' : undefined
                  }
                  data-testid={`score-pill-left-${setIndex}`}
                >
                  {s.left !== null ? s.left : SCORE_PLACEHOLDER}
                </button>
                <span className="score-separator">–</span>
                <button
                  className={`score-pill ${!isValid ? 'invalid' : ''} ${
                    !canEdit ? 'read-only' : ''
                  } ${hasRightValue ? 'has-value' : ''}`}
                  onClick={() => canEdit && onSetClick(setIndex)}
                  disabled={!canEdit}
                  aria-label={`Enter score for set ${setIndex + 1}, ${playerNames.right}${!canEdit ? ' (match finalized)' : ''}`}
                  aria-describedby={
                    !canEdit ? 'match-finalized-notice' : undefined
                  }
                  data-testid={`score-pill-right-${setIndex}`}
                >
                  {s.right !== null ? s.right : SCORE_PLACEHOLDER}
                </button>
                {showValidationErrors && setErrors[setIndex] && (
                  <div className="set-error-message" role="alert">
                    {setErrors[setIndex]}
                  </div>
                )}
              </div>
            )
          })}

          {/* Hidden notice for screen readers when match is finalized */}
          {!canEdit && (
            <div id="match-finalized-notice" className="sr-only">
              This match has been finalized and scores cannot be changed.
            </div>
          )}

          {/* Prioritized Validation Error Display */}
          {canEdit && showValidationErrors && validationErrors.length > 0 && (
            <div className="validation-errors mt-3">
              {prioritizedError ? (
                <div className="alert alert-warning">
                  <div className="d-flex align-items-start">
                    <span
                      className="me-2"
                      style={{ fontSize: '1.2em' }}
                      aria-hidden="true"
                    >
                      ⚠️
                    </span>
                    <div>
                      <strong>{prioritizedError.message}</strong>
                      <div className="mt-1 small text-muted">
                        {prioritizedError.guidance}
                      </div>
                      {validationErrors.length > 1 && (
                        <div className="mt-2">
                          <details>
                            <summary
                              className="small text-muted"
                              style={{ cursor: 'pointer' }}
                            >
                              View all issues ({validationErrors.length})
                            </summary>
                            <ul className="mb-0 mt-2 small">
                              {validationErrors.map((error, index) => (
                                <li key={index}>{error}</li>
                              ))}
                            </ul>
                          </details>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="alert alert-danger">
                  <strong>Please fix the following issues:</strong>
                  <ul className="mb-0 mt-2">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Match Summary - pre-submit confirmation */}
          {canEdit && (
            <div className="match-summary mt-3 mb-0">
              <div className="text-muted small mb-1">Match Summary:</div>
              <div className="summary-text">{matchSummary}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

ScoreEntryCard.propTypes = {
  sets: PropTypes.arrayOf(
    PropTypes.shape({
      left: PropTypes.number,
      right: PropTypes.number,
    }),
  ).isRequired,
  playerNames: PropTypes.shape({
    left: PropTypes.string.isRequired,
    right: PropTypes.string.isRequired,
  }).isRequired,
  matchData: PropTypes.shape({
    status: PropTypes.string,
    submitted_by_first_name: PropTypes.string,
    submitted_by_last_name: PropTypes.string,
    away_player_id: PropTypes.number,
    home_player_id: PropTypes.number,
    division_id: PropTypes.number,
  }),
  visibleSets: PropTypes.arrayOf(PropTypes.number).isRequired,
  canEdit: PropTypes.bool.isRequired,
  validationErrors: PropTypes.arrayOf(PropTypes.string).isRequired,
  prioritizedError: PropTypes.shape({
    message: PropTypes.string.isRequired,
    guidance: PropTypes.string.isRequired,
  }),
  showValidationErrors: PropTypes.bool.isRequired,
  matchSummary: PropTypes.string.isRequired,
  onSetClick: PropTypes.func.isRequired,
  isFinalized: PropTypes.bool.isRequired,
  leagueId: PropTypes.string.isRequired,
  setErrors: PropTypes.object.isRequired,
  set3Ref: PropTypes.object,
  navigate: PropTypes.func.isRequired,
  matchFormat: PropTypes.string,
}
